import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/db.ts';
import { logger } from '../../services/logger.ts';

// Manually defined type based on our migration.
// Ideally, this would come from a generated Supabase types file.
interface CustomerServiceRequest {
  id: string;
  user_id: string | null;
  email: string;
  subject: string;
  message: string;
  status: 'open' | 'pending' | 'resolved' | 'closed';
  created_at: string;
  updated_at: string;
}

export function CustomerServiceQueue() {
  const [requests, setRequests] = useState<CustomerServiceRequest[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<CustomerServiceRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [replySending, setReplySending] = useState(false);
  const [replySuccess, setReplySuccess] = useState(false);

  useEffect(() => {
    // The checkAuthAndFetch structure can be simplified if the functions handle auth internally
    const fetchRequestsRpc = async () => {
      setLoading(true);
      setError(null);
      try {
        // Call the RPC function
        const { data, error: rpcError } = await supabase.rpc('get_all_customer_service_requests_admin');

        if (rpcError) throw rpcError;
        setRequests(data || []);
      } catch (err: unknown) {
        logger.error('Error fetching customer service requests via RPC:', err);
        if (err instanceof Error) {
          setError(err.message || 'Failed to fetch requests.');
        } else {
          setError('An unknown error occurred while fetching requests.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchRequestsRpc();
  }, []);

  const handleViewDetails = (request: CustomerServiceRequest) => {
    setSelectedRequest(request);
    setReplyText('');
    setReplySuccess(false);
  };

  const handleSendReply = async () => {
    if (!selectedRequest || !replyText.trim()) return;
    setReplySending(true);
    setReplySuccess(false);
    try {
      const { error: rpcError } = await supabase.rpc('submit_admin_customer_service_reply', {
        p_request_id: selectedRequest.id,
        p_reply_text: replyText.trim(),
      });
      if (rpcError) throw rpcError;
      setReplySuccess(true);
      setReplyText('');
      // Auto-move to pending if still open
      if (selectedRequest.status === 'open') {
        await handleStatusChange(selectedRequest.id, 'pending');
      }
    } catch (err: unknown) {
      logger.error('Error sending reply:', err);
      alert(err instanceof Error ? err.message : 'Failed to send reply');
    } finally {
      setReplySending(false);
    }
  };

  const handleStatusChange = async (requestId: string, newStatus: CustomerServiceRequest['status']) => {
    const originalRequests = [...requests];
    const originalSelectedRequest = selectedRequest ? { ...selectedRequest } : null;

    // Optimistic UI update
    setRequests(prevRequests =>
      prevRequests.map(req =>
        req.id === requestId ? { ...req, status: newStatus, updated_at: new Date().toISOString() } : req
      )
    );
    if (selectedRequest && selectedRequest.id === requestId) {
      setSelectedRequest(prev => prev ? { ...prev, status: newStatus, updated_at: new Date().toISOString() } : null);
    }

    try {
      // Call the RPC function to update status
      const { data: updatedRequest, error: rpcError } = await supabase.rpc('update_customer_service_request_status_admin', {
        request_id_to_update: requestId,
        new_status: newStatus
      });

      if (rpcError) throw rpcError;

      // Optionally, update the state with the exact data returned from the RPC for consistency
      if (updatedRequest) {
        setRequests(prevRequests =>
          prevRequests.map(req => (req.id === updatedRequest.id ? updatedRequest : req))
        );
        if (selectedRequest && selectedRequest.id === updatedRequest.id) {
            setSelectedRequest(updatedRequest);
        }
      }

    } catch (err: unknown) {
      logger.error('Error updating request status via RPC:', err);
      // Revert optimistic update on error
      setRequests(originalRequests);
      if (originalSelectedRequest) setSelectedRequest(originalSelectedRequest);

      if (err instanceof Error) {
        setError(err.message || 'Failed to update status.');
      } else {
        setError('An unknown error occurred while updating status.');
      }
    }
  };

  if (loading) {
    return (
      <div className="bg-white p-8 rounded-lg shadow-md flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-wayja-teal"></div>
        <p className="ml-4 text-gray-600">Loading requests...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white p-8 rounded-lg shadow-md">
        <h2 className="text-2xl font-bold mb-6 text-red-600">Error</h2>
        <div className="p-8 bg-red-50 rounded-lg flex items-center justify-center">
          <p className="text-red-700">{error}</p>
        </div>
      </div>
    );
  }

  if (selectedRequest) {
    return (
      <div className="bg-white p-8 rounded-lg shadow-md">
        <button
          onClick={() => setSelectedRequest(null)}
          className="mb-6 text-wayja-teal hover:text-wayja-dark-teal font-medium flex items-center gap-2 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to All Requests
        </button>
        <h2 className="text-2xl font-bold mb-4">{selectedRequest.subject}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <p><strong>User:</strong> {selectedRequest.email} {selectedRequest.user_id ? `(${selectedRequest.user_id})` : ''}</p>
          <p><strong>Status:</strong> <span className={`px-2 py-1 text-xs font-medium rounded-full ${
            selectedRequest.status === 'open' ? 'bg-red-100 text-red-700' :
            selectedRequest.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
            selectedRequest.status === 'resolved' ? 'bg-green-100 text-green-700' :
            'bg-gray-100 text-gray-700'
          }`}>{selectedRequest.status}</span></p>
          <p><strong>Requested:</strong> {new Date(selectedRequest.created_at).toLocaleString()}</p>
        </div>
        <div className="bg-gray-50 p-4 rounded-md mb-6">
          <h3 className="font-semibold mb-2">Message:</h3>
          <p className="text-gray-700 whitespace-pre-wrap">{selectedRequest.message}</p>
        </div>

        <div className="flex gap-4 mb-6">
          <select
            value={selectedRequest.status}
            onChange={(e) => handleStatusChange(selectedRequest.id, e.target.value as CustomerServiceRequest['status'])}
            className="p-2 border border-gray-300 rounded-md bg-white"
          >
            <option value="open">Open</option>
            <option value="pending">Pending</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </div>

        <div className="border rounded-md p-4 bg-gray-50">
          <h3 className="font-semibold mb-2 text-sm text-gray-700">Reply to customer</h3>
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Type your reply here..."
            rows={4}
            className="w-full border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {replySuccess && (
            <p className="text-green-600 text-sm mt-1">Reply sent successfully.</p>
          )}
          <div className="mt-2 flex justify-end">
            <button
              onClick={handleSendReply}
              disabled={replySending || !replyText.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              {replySending ? 'Sending...' : 'Send Reply'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-8 rounded-lg shadow-md">
      <h2 className="text-2xl font-bold mb-6">Customer Service Moderation Queue</h2>
      {requests.length === 0 && !loading ? (
        <div className="p-8 bg-gray-100 rounded-lg flex items-center justify-center">
          <p className="text-gray-500">No customer service requests at the moment.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User (Email/ID)</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Updated</th>
                <th scope="col" className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {requests.map((request) => (
                <tr key={request.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{request.subject}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{request.email}</td> 
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      request.status === 'open' ? 'bg-red-100 text-red-700' :
                      request.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                      request.status === 'resolved' ? 'bg-green-100 text-green-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {request.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(request.updated_at).toLocaleString()}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button onClick={() => handleViewDetails(request)} className="text-blue-600 hover:text-blue-900">
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
} 